import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { apiFetch } from '../lib/api-client';
import type { ActiveCall } from './communications-model';
import { CallOutcomeActions, callOutcomeLabel } from './CallOutcomeActions';

/** Viewing a completed call never claims it or changes who answered it. */
export function CallReviewActions({ callId, onSaved, canCancelJob = false }: { callId: string; onSaved: (call: ActiveCall) => void; canCancelJob?: boolean }) {
 const [call, setCall] = useState<ActiveCall | null>(null);
 const [error, setError] = useState('');
 const [unavailable, setUnavailable] = useState(false);
 const [busy, setBusy] = useState(false);
 const [takeover, setTakeover] = useState(false);
 useEffect(() => {
  const controller = new AbortController();
  void apiFetch(`/api/v1/workspace/communications/live-calls/${encodeURIComponent(callId)}`, { signal: controller.signal }).then(async response => {
   // Older API releases expose pending calls through the list only.
   if (response.status === 404) {
    const pending = await apiFetch('/api/v1/workspace/communications/live-calls', { signal: controller.signal });
    if (!pending.ok) throw new Error('Call details could not be loaded.');
    const payload = await pending.json();
    const existing = (payload.data?.items as ActiveCall[] | undefined)?.find(item => item.id === callId);
    if (!controller.signal.aborted) { setCall(existing ?? null); setUnavailable(!existing); }
    return;
   }
   if (!response.ok) throw new Error('Call details could not be loaded.');
   const payload = await response.json();
   if (!controller.signal.aborted) setCall(payload.data);
  }).catch(cause => { if (!controller.signal.aborted) setError(cause.message); });
  return () => controller.abort();
 }, [callId]);
 async function claim() {
  if (!call || busy) return;
  setBusy(true); setError('');
  try {
   const response = await apiFetch(`/api/v1/workspace/communications/live-calls/${encodeURIComponent(callId)}/claim`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(call.assignedToUserId ? { takeover: true, previousUserId: call.assignedToUserId } : {}),
   });
   const payload = await response.json();
   if (!response.ok) throw new Error(payload.error?.message || 'This call could not be selected.');
   setCall(payload.data); setTakeover(false);
  } catch (cause) { setError(cause instanceof Error ? cause.message : 'This call could not be selected.'); }
  finally { setBusy(false); }
 }
 async function release() {
  if(!call || busy)return;
  setBusy(true);setError('');
  try {
   const response=await apiFetch(`/api/v1/workspace/communications/live-calls/${encodeURIComponent(callId)}/release`,{method:'POST'});
   if(!response.ok){const payload=await response.json().catch(()=>null);throw new Error(payload?.error?.message || 'This call could not be released.');}
   setCall({...call,assignedToUserId:null,assignedToName:null,isMine:false});
   window.dispatchEvent(new Event('workview:calls-changed'));
  }catch(cause){setError(cause instanceof Error?cause.message:'This call could not be released.');}
  finally{setBusy(false);}
 }
 if (unavailable) return null;
 return <section className="comms-call-review" aria-label="Call handling">
  <h3>Call outcome</h3>
  {call?.isMine && !call.outcome && <button type="button" className="comms-call-release" aria-label="Release call" title="Release call" disabled={busy} onClick={()=>void release()}><X size={14} aria-hidden="true" /></button>}
  {error && <p role="alert" className="comms-review-error">{error}</p>}
  {!call && !error && <p role="status">Loading call actions…</p>}
  {call?.customerName && <p className="comms-call-linked">{call.customerName}</p>}
  {call?.customerId && call.locationId && <a className="comms-link" href={`/dashboard?customer=${encodeURIComponent(call.customerId)}&location=${encodeURIComponent(call.locationId)}`}>Open location</a>}
  {call?.locationAddress && <p className="comms-call-linked">{call.locationAddress}</p>}
  {call?.outcome ? <p>{callOutcomeLabel(call.outcome)}{call.notes ? ` · ${call.notes}` : ''}</p> : call?.isMine ?
   <CallOutcomeActions key={call.id} call={call} canCancelJob={canCancelJob} onSaved={saved => { setCall(saved); onSaved(saved); }} /> : call && <>
    <p className="comms-muted">{call.assignedToName ? `${call.assignedToName} is handling this call.` : 'Select this call to record its outcome.'}</p>
    {takeover ? <div role="alertdialog" aria-label="Take over call"><p>Take over from {call.assignedToName || 'Office staff'}?</p><button className="comms-button" disabled={busy} onClick={() => void claim()}>Confirm takeover</button><button className="comms-link" disabled={busy} onClick={() => setTakeover(false)}>Cancel</button></div> :
     <button className="comms-button" disabled={busy} onClick={() => call.assignedToUserId ? setTakeover(true) : void claim()}>{busy ? 'Selecting…' : call.assignedToUserId ? 'Take over call' : 'Handle call'}</button>}
   </>}
 </section>;
}
